import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_BASE_URL = `${window.location.protocol}//${window.location.hostname}/api`;

function api(path, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers }
  }).then(r => r.json());
}

const STRAND_COLORS = ['blue','orange','green','brown','slate','white','red','black','yellow','violet','rose','aqua'];
const COLOR_HEX = {
  blue:'#3b82f6', orange:'#f97316', green:'#22c55e', brown:'#92400e',
  slate:'#94a3b8', white:'#e2e8f0', red:'#ef4444', black:'#6b7280',
  yellow:'#eab308', violet:'#a855f7', rose:'#fb7185', aqua:'#22d3ee'
};
const STATUS_CYCLE = { free: 'used', used: 'fault', fault: 'free' };

function getStrandColor(port) {
  return COLOR_HEX[port.strand_color || STRAND_COLORS[port.port_number - 1]] || '#94a3b8';
}

function portBg(port, selected) {
  if (selected) return '#ffffff33';
  if (port.status === 'fault') return '#ef4444';
  if (port.status === 'used') return getStrandColor(port);
  return '#0d1117';
}

function portBorder(port, selected) {
  if (selected) return '2px solid #ffffff';
  if (port.status === 'fault') return '2px solid #fca5a5';
  if (port.status === 'used') return `2px solid ${getStrandColor(port)}`;
  return `1px solid ${getStrandColor(port)}99`;
}

function portShadow(port, selected) {
  if (selected) return '0 0 0 2px #ffffff88, 0 0 10px #ffffff44';
  if (port.status === 'fault') return '0 0 8px rgba(239,68,68,0.9)';
  if (port.status === 'used') return `0 0 6px ${getStrandColor(port)}99`;
  return 'none';
}

// ── Single port hole ──────────────────────────────────────────────────────────
function PortHole({ port, connectorType, size, selected, onSelect }) {
  const ct = (connectorType || 'SC').toUpperCase();
  const style = {
    backgroundColor: portBg(port, selected),
    border: portBorder(port, selected),
    boxShadow: portShadow(port, selected),
    cursor: 'pointer',
    transition: 'all 0.15s',
    flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  const shapeStyle = ct === 'ST' || ct === 'FC'
    ? { width: size, height: size, borderRadius: '50%' }
    : ct === 'LC'
    ? { width: Math.round(size * 0.65), height: size, borderRadius: 2 }
    : ct === 'MPO'
    ? { width: size * 2 + 4, height: Math.round(size * 0.6), borderRadius: 3 }
    : { width: size, height: size, borderRadius: 3 }; // SC default

  return (
    <div
      onClick={onSelect}
      title={`Port ${port.port_number} · ${port.strand_color || ''} · ${port.status}`}
      style={{ ...style, ...shapeStyle }}
    />
  );
}

// ── Cassette face ─────────────────────────────────────────────────────────────
// orientation='vertical'   → 6 rows × 2 cols  (4U narrow columns)
// orientation='horizontal' → 6 cols × 2 rows  (2U wide slots)
function CassetteFace({ cassetteId, connectorType, portSize, selectedPorts, onTogglePort, orientation = 'vertical' }) {
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api(`/cassettes/${cassetteId}/ports`).then(data => {
      setPorts(Array.isArray(data) ? data : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [cassetteId]);

  useEffect(() => {
    if (window.__portSetters) window.__portSetters[cassetteId] = setPorts;
    return () => { if (window.__portSetters) delete window.__portSetters[cassetteId]; };
  }, [cassetteId]);

  const pairs = [];
  for (let i = 0; i < 12; i += 2) pairs.push([ports[i], ports[i + 1]].filter(Boolean));

  const skeletonDot = { width: portSize, height: portSize, borderRadius: '50%', backgroundColor: '#1e293b' };

  if (loading) {
    return orientation === 'horizontal' ? (
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', padding: '8px 4px' }}>
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={skeletonDot} /><div style={skeletonDot} />
          </div>
        ))}
      </div>
    ) : (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', padding: '6px 4px' }}>
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
            <div style={skeletonDot} /><div style={skeletonDot} />
          </div>
        ))}
      </div>
    );
  }

  if (orientation === 'horizontal') {
    const ct = (connectorType || 'SC').toUpperCase();
    const isCircle = ct === 'ST' || ct === 'FC';
    // CSS grid: 6 columns × 2 rows, fills all available space
    // gridAutoFlow column means pairs fill down first: col1=[p1,p2], col2=[p3,p4]...
    return (
      <div style={{
        flex: 1, minHeight: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gridTemplateRows: '1fr 1fr',
        gridAutoFlow: 'column',
        gap: 10,
        padding: '12px 14px',
      }}>
        {ports.map(port => (
          <div
            key={port.id}
            onClick={() => onTogglePort(port)}
            title={`Port ${port.port_number} · ${port.strand_color} · ${port.status}`}
            style={{
              borderRadius: isCircle ? '50%' : ct === 'LC' ? 4 : 6,
              backgroundColor: portBg(port, selectedPorts.has(port.id)),
              border: portBorder(port, selectedPorts.has(port.id)),
              boxShadow: portShadow(port, selectedPorts.has(port.id)),
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          />
        ))}
      </div>
    );
  }

  // Vertical: 6 rows × 2 cols, evenly spaced top-to-bottom
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', padding: '4px', width: '100%', alignItems: 'center' }}>
      {pairs.map((pair, ri) => (
        <div key={ri} style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          {pair.map(port => (
            <PortHole key={port.id} port={port} connectorType={connectorType} size={portSize}
              selected={selectedPorts.has(port.id)} onSelect={() => onTogglePort(port)} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Cassette module (the physical black panel) ────────────────────────────────
function CassetteModule({ slot, cassette, onClickEmpty, portSize, selectedPorts, onTogglePort, onEdit, onDelete }) {
  if (!cassette) {
    return (
      <div
        onClick={() => onClickEmpty(slot)}
        style={{
          backgroundColor: '#1a2035', borderRadius: 6,
          border: '1px dashed #2d3f5e',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: '100%', minHeight: 180, cursor: 'pointer',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = '#4a6080'}
        onMouseLeave={e => e.currentTarget.style.borderColor = '#2d3f5e'}
      >
        <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: '#2d3f5e', fontSize: 11 }}>+ Add</div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#1c2333', borderRadius: 6, border: '1px solid #2a3f5f',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      boxShadow: '0 2px 8px rgba(0,0,0,0.4)', height: '100%',
      position: 'relative', overflow: 'visible',
    }}>

      {/* Connector type + edit/delete bar */}
      <div style={{ width: '100%', backgroundColor: '#243050', borderRadius: '6px 6px 0 0', borderBottom: '1px solid #2a3f5f', padding: '4px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ fontSize: 9, fontWeight: 'bold', color: '#60a5fa', letterSpacing: '0.06em' }}>{cassette.connector_type}</span>
        <div style={{ display: 'flex', gap: 2 }}>
          <button
            onClick={e => { e.stopPropagation(); onEdit(cassette); }}
            title="Edit cassette"
            style={{ backgroundColor: 'transparent', border: 'none', color: '#60a5fa', fontSize: 11, cursor: 'pointer', padding: '1px 3px', borderRadius: 3, lineHeight: 1 }}
          >✏️</button>
          <button
            onClick={e => { e.stopPropagation(); onDelete(cassette); }}
            title="Delete cassette"
            style={{ backgroundColor: 'transparent', border: 'none', color: '#f87171', fontSize: 11, cursor: 'pointer', padding: '1px 3px', borderRadius: 3, lineHeight: 1 }}
          >🗑</button>
        </div>
      </div>

      {/* Top mount hole */}
      <div style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: '#0d1117', border: '1px solid #374151', marginTop: 5, flexShrink: 0 }} />

      {/* Ports */}
      <CassetteFace
        cassetteId={cassette.id}
        connectorType={cassette.connector_type}
        portSize={portSize}
        selectedPorts={selectedPorts}
        onTogglePort={onTogglePort}
      />

      {/* Bottom mount hole */}
      <div style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: '#0d1117', border: '1px solid #374151', marginBottom: 4, flexShrink: 0 }} />

      {/* Cable label strip — horizontal, centered, shrinks to fit */}
      <div style={{ width: '100%', backgroundColor: '#1a2540', borderTop: '1px solid #2a3f5f', padding: '4px 3px', textAlign: 'center', flexShrink: 0, borderRadius: '0 0 6px 6px' }}>
        <div style={{
          color: '#94a3b8',
          fontSize: 'clamp(6px, 0.7vw, 10px)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          lineHeight: 1.2,
        }}>
          {cassette.cable_label || slot}
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function CassetteView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [housing, setHousing] = useState(null);
  const [cassettes, setCassettes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingSlot, setAddingSlot] = useState(null);
  const [newCassette, setNewCassette] = useState({ slot_label: '', connector_type: 'SC', cable_label: '' });
  const [editingCassette, setEditingCassette] = useState(null);
  const [deletingCassette, setDeletingCassette] = useState(null);
  const [selectedPorts, setSelectedPorts] = useState(new Set());
  const [portCache, setPortCache] = useState({}); // cassetteId → ports[]

  // Init port setter registry
  useEffect(() => { window.__portSetters = {}; return () => { window.__portSetters = {}; }; }, []);

  useEffect(() => {
    Promise.all([
      api(`/housings/${id}`),
      api(`/cassettes/housing/${id}`)
    ]).then(([h, c]) => {
      setHousing(h);
      setCassettes(Array.isArray(c) ? c : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const totalSlots = housing?.chassis_type === '12-slot' ? 12 : 4;
  const slotLabels = Array.from({ length: totalSlots }, (_, i) => String.fromCharCode(65 + i));
  const is4Slot = housing?.chassis_type === '4-slot';

  // Toggle port selection
  const togglePort = useCallback((port) => {
    setSelectedPorts(prev => {
      const next = new Set(prev);
      if (next.has(port.id)) next.delete(port.id);
      else next.add(port.id);
      return next;
    });
  }, []);

  // Batch status change for all selected ports
  const applyStatusToSelected = useCallback(async (status) => {
    const ids = [...selectedPorts];
    // Optimistic update in all CassetteFace instances
    Object.values(window.__portSetters || {}).forEach(setter => {
      setter(prev => prev.map(p => ids.includes(p.id) ? { ...p, status } : p));
    });
    setSelectedPorts(new Set());
    // Persist
    await Promise.all(ids.map(portId =>
      api(`/cassettes/ports/${portId}`, { method: 'PATCH', body: JSON.stringify({ status }) })
    ));
  }, [selectedPorts]);

  // Add cassette
  const addCassette = async () => {
    if (!newCassette.slot_label) return;
    const result = await api('/cassettes', {
      method: 'POST',
      body: JSON.stringify({ housing_id: parseInt(id), ...newCassette })
    });
    setCassettes(prev => [...prev, result]);
    setAddingSlot(null);
    setNewCassette({ slot_label: '', connector_type: 'SC', cable_label: '' });
  };

  // Edit cassette
  const saveCassetteEdit = async () => {
    if (!editingCassette) return;
    const result = await api(`/cassettes/${editingCassette.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ cable_label: editingCassette.cable_label, connector_type: editingCassette.connector_type })
    });
    setCassettes(prev => prev.map(c => c.id === editingCassette.id ? { ...c, ...result } : c));
    setEditingCassette(null);
  };

  // Delete cassette
  const confirmDeleteCassette = async () => {
    if (!deletingCassette) return;
    await api(`/cassettes/${deletingCassette.id}`, { method: 'DELETE' });
    setCassettes(prev => prev.filter(c => c.id !== deletingCassette.id));
    setDeletingCassette(null);
  };

  if (loading) return <div style={{ padding: 32, color: 'white', backgroundColor: '#0f172a', minHeight: '100vh', fontFamily: 'sans-serif' }}>Loading...</div>;
  if (!housing) return <div style={{ padding: 32, color: 'white', backgroundColor: '#0f172a', minHeight: '100vh', fontFamily: 'sans-serif' }}>Housing not found.</div>;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#111827', fontFamily: 'sans-serif', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ backgroundColor: '#1e293b', color: 'white', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 16, borderBottom: '1px solid #334155', flexShrink: 0 }}>
        <button onClick={() => navigate(-1)} style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid #334155', color: 'white', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>← Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', fontSize: 16 }}>{housing.label || 'Housing'}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{is4Slot ? '2U · 4-Slot' : '4U · 12-Slot'} · {housing.manufacturer || ''} {housing.model || ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#94a3b8', alignItems: 'center' }}>
          <span>Slots: <b style={{ color: 'white' }}>{totalSlots}</b></span>
          <span>Installed: <b style={{ color: 'white' }}>{cassettes.length}</b></span>
        </div>
        <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#94a3b8', alignItems: 'center' }}>
          <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:'50%', backgroundColor:'#0f172a', border:'1px solid #3b82f666', display:'inline-block' }} /> Free</span>
          <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:'50%', backgroundColor:'#3b82f6', display:'inline-block' }} /> Used</span>
          <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:'50%', backgroundColor:'#ef4444', display:'inline-block', boxShadow:'0 0 6px rgba(239,68,68,0.8)' }} /> Fault</span>
          <span style={{ display:'flex', alignItems:'center', gap:5 }}><span style={{ width:10, height:10, borderRadius:2, backgroundColor:'#ffffff22', border:'2px solid white', display:'inline-block' }} /> Selected</span>
        </div>
        <button onClick={() => navigate(`/print/${id}`)} style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid #334155', color: 'white', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>🖨 QR Label</button>
      </div>

      {/* Selection action bar */}
      {selectedPorts.size > 0 && (
        <div style={{ backgroundColor: '#1e3a5f', borderBottom: '1px solid #2563eb', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <span style={{ color: '#93c5fd', fontSize: 13, fontWeight: 'bold' }}>{selectedPorts.size} port{selectedPorts.size > 1 ? 's' : ''} selected</span>
          <span style={{ color: '#334155', fontSize: 13 }}>Set status:</span>
          {['free','used','fault'].map(s => (
            <button key={s} onClick={() => applyStatusToSelected(s)} style={{
              padding: '5px 14px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 'bold',
              backgroundColor: s === 'free' ? '#1e293b' : s === 'used' ? '#2563eb' : '#dc2626',
              color: 'white', textTransform: 'capitalize',
            }}>{s}</button>
          ))}
          <button onClick={() => setSelectedPorts(new Set())} style={{ marginLeft: 'auto', padding: '5px 12px', borderRadius: 5, border: '1px solid #334155', backgroundColor: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}>✕ Clear selection</button>
        </div>
      )}

      {/* Chassis */}
      <div style={{ flex: 1, padding: 16, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{
          flex: 1, backgroundColor: '#161f30', borderRadius: 10,
          border: '2px solid #2a3f5f', padding: '16px',
          boxShadow: '0 4px 32px rgba(0,0,0,0.7)',
          display: 'flex', flexDirection: 'column', minHeight: 0,
        }}>
          <div style={{ color: '#334155', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, flexShrink: 0 }}>
            {is4Slot ? '2U Horizontal · 4-Slot' : '4U Vertical · 12-Slot'} Fiber Chassis
          </div>

          {/* Port strand color border is more visible on lighter base */}

          {is4Slot ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 12, flex: 1, minHeight: 0 }}>
              {slotLabels.map(slot => {
                const cassette = cassettes.find(c => c.slot_label === slot);
                return (
                  <div key={slot} style={{ display: 'flex', flexDirection: 'column', gap: 5, minHeight: 0, height: '100%' }}>
                    <div style={{ color: '#475569', fontSize: 10, fontWeight: 'bold', letterSpacing: '0.08em', flexShrink: 0 }}>SLOT {slot}</div>
                    {cassette ? (
                      <div style={{ flex: 1, backgroundColor: '#1c2333', borderRadius: 6, border: '1px solid #2a3f5f', display: 'flex', flexDirection: 'column', position: 'relative', minHeight: 0, overflow: 'hidden', height: '100%' }}>
                        {/* Header bar */}
                        <div style={{ backgroundColor: '#243050', borderBottom: '1px solid #2a3f5f', padding: '5px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                          <span style={{ color: '#60a5fa', fontSize: 10, fontWeight: 'bold', letterSpacing: '0.06em' }}>{cassette.connector_type}</span>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => setEditingCassette({ ...cassette })} style={{ backgroundColor: 'transparent', border: 'none', color: '#60a5fa', fontSize: 13, cursor: 'pointer', padding: '0 2px' }}>✏️</button>
                            <button onClick={() => setDeletingCassette(cassette)} style={{ backgroundColor: 'transparent', border: 'none', color: '#f87171', fontSize: 13, cursor: 'pointer', padding: '0 2px' }}>🗑</button>
                          </div>
                        </div>
                        {/* Cable label — centered, shrinks to fit */}
                        <div style={{ padding: '6px 10px 2px', textAlign: 'center', flexShrink: 0 }}>
                          <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 'clamp(9px, 1.2vw, 14px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {cassette.cable_label || `Slot ${slot}`}
                          </div>
                        </div>
                        {/* Ports — horizontal layout */}
                        <CassetteFace cassetteId={cassette.id} connectorType={cassette.connector_type} portSize={24} selectedPorts={selectedPorts} onTogglePort={togglePort} orientation="horizontal" />
                      </div>
                    ) : (
                      <div onClick={() => { setAddingSlot(slot); setNewCassette(p => ({ ...p, slot_label: slot })); }}
                        style={{ flex: 1, border: '1px dashed #1e3a5f', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1e3a5f', fontSize: 13, cursor: 'pointer', minHeight: 80 }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#334155'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = '#1e3a5f'}>
                        + Add cassette
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 6, flex: 1, minHeight: 0 }}>
              {slotLabels.map(slot => {
                const cassette = cassettes.find(c => c.slot_label === slot);
                return (
                  <div key={slot} style={{ display: 'flex', flexDirection: 'column', gap: 4, minHeight: 0 }}>
                    <div style={{ color: '#475569', fontSize: 9, fontWeight: 'bold', textAlign: 'center', letterSpacing: '0.05em', flexShrink: 0 }}>{slot}</div>
                    <div style={{ flex: 1, minHeight: 0 }}>
                      <CassetteModule
                        slot={slot}
                        cassette={cassette}
                        onClickEmpty={s => { setAddingSlot(s); setNewCassette(p => ({ ...p, slot_label: s })); }}
                        portSize={20}
                        selectedPorts={selectedPorts}
                        onTogglePort={togglePort}
                        onEdit={c => setEditingCassette({ ...c })}
                        onDelete={c => setDeletingCassette(c)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add cassette modal */}
      {addingSlot && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, width: 320, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 16 }}>Add Cassette — Slot {addingSlot}</div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>Cable label</label>
              <input autoFocus type="text" placeholder="e.g. Cable 12 East" value={newCassette.cable_label}
                onChange={e => setNewCassette(p => ({ ...p, cable_label: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addCassette()}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>Connector type</label>
              <select value={newCassette.connector_type} onChange={e => setNewCassette(p => ({ ...p, connector_type: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13 }}>
                {['SC','ST','FC','LC','MPO'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setAddingSlot(null)} style={{ flex: 1, padding: 9, backgroundColor: '#edf2f7', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={addCassette} style={{ flex: 1, padding: 9, backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit cassette modal */}
      {editingCassette && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, width: 320, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 16 }}>Edit Cassette — Slot {editingCassette.slot_label}</div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>Cable label</label>
              <input autoFocus type="text" value={editingCassette.cable_label || ''}
                onChange={e => setEditingCassette(p => ({ ...p, cable_label: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && saveCassetteEdit()}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>Connector type</label>
              <select value={editingCassette.connector_type} onChange={e => setEditingCassette(p => ({ ...p, connector_type: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13 }}>
                {['SC','ST','FC','LC','MPO'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setEditingCassette(null)} style={{ flex: 1, padding: 9, backgroundColor: '#edf2f7', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={saveCassetteEdit} style={{ flex: 1, padding: 9, backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete cassette confirm */}
      {deletingCassette && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: 'white', borderRadius: 12, padding: 24, width: 320, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 8, color: '#c53030' }}>Delete Cassette?</div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 20 }}>
              Delete <b>{deletingCassette.cable_label || `Slot ${deletingCassette.slot_label}`}</b>? This will remove all port records. Cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setDeletingCassette(null)} style={{ flex: 1, padding: 9, backgroundColor: '#edf2f7', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={confirmDeleteCassette} style={{ flex: 1, padding: 9, backgroundColor: '#e53e3e', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
